/**
 * Image fade slideshow, used by sections/image-slideshow-with-text.liquid.
 *
 * All slides are stacked in the same grid cell; the active one gets `is-active` and CSS fades it in.
 * Rotation only runs while the section is on screen and the tab is visible. In the theme editor,
 * selecting an image block jumps to that slide and holds it until the block is deselected.
 */
class ImageFadeSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.image-fade-slideshow__slide'));
    if (this.slides.length < 2) return;

    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Math.max(2, Number(this.getAttribute('interval')) || 5) * 1000;
    this.isVisible = false;
    this.isHeld = false;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    document.addEventListener('visibilitychange', () => this.update(), { signal });
    document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event, true), { signal });
    document.addEventListener('shopify:block:deselect', (event) => this.onBlockSelect(event, false), { signal });

    this.observer = new IntersectionObserver(([entry]) => {
      this.isVisible = Boolean(entry?.isIntersecting);
      this.update();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
    this.stop();
  }

  update() {
    if (this.isVisible && !this.isHeld && document.visibilityState === 'visible') {
      this.start();
    } else {
      this.stop();
    }
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  show(index) {
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      const isActive = i === this.index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
  }

  onBlockSelect(event, isSelected) {
    const slideIndex = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
    if (slideIndex === -1) return;

    this.isHeld = isSelected;
    if (isSelected) this.show(slideIndex);
    this.update();
  }
}

if (!window.customElements.get('image-fade-slideshow')) {
  window.customElements.define('image-fade-slideshow', ImageFadeSlideshow);
}
